import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import clsx from "clsx";
import { motion } from "framer-motion";
import { CheckCircle } from "lucide-react";
import { useState } from "react";
import { z } from "zod";

import { usePasswordResetConfirmMutation } from "@/api/auth/usePasswordResetConfirmMutation";
import { Logo } from "@/components/brand/Logo";
import toast from "@/components/feedback/toast";
import { BackgroundVideoPlaylist } from "@/components/layout/BackgroundVideoPlaylist";

const searchSchema = z.object({
  token: z.string().catch(""),
});

export const Route = createFileRoute("/reset-password")({
  validateSearch: searchSchema,
  component: RouteComponent,
});

function PasswordRule({ met, label }: { met: boolean; label: string }) {
  return (
    <div className="flex items-center gap-2 text-[11px]">
      <div
        className={clsx(
          "size-1.5 rounded-full transition-colors",
          met ? "bg-green-400" : "bg-white/15",
        )}
      />
      <span className={met ? "text-white/50" : "text-white/25"}>
        {label}
      </span>
    </div>
  );
}

function RouteComponent() {
  const { token } = useSearch({ from: "/reset-password" });
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [done, setDone] = useState(false);
  const resetConfirm = usePasswordResetConfirmMutation();

  const rules = [
    { met: password.length >= 8, label: "Min. 8 znaków" },
    { met: /[a-z]/.test(password), label: "Mała litera" },
    { met: /[A-Z]/.test(password), label: "Wielka litera" },
    { met: /\d/.test(password), label: "Cyfra" },
    { met: /[^A-Za-z0-9]/.test(password), label: "Znak specjalny" },
  ];
  const passwordValid = rules.every((r) => r.met);
  const passwordsMatch = password === confirmPassword;
  const canSubmit = passwordValid && passwordsMatch && !!token;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await resetConfirm.mutateAsync({ token, password });
      setDone(true);
    } catch {
      toast({ message: "Operacja nie powiodła się.", type: "error" });
    }
  };

  if (!token) {
    return (
      <div className="h-dvh bg-black flex items-center justify-center px-6">
        <div className="text-center space-y-4">
          <p className="text-white/40 text-sm">Brak tokenu resetowania hasła.</p>
          <Link to="/login" className="text-xs text-white/30 hover:text-white/60 transition-colors">
            ← Wróć do logowania
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="h-dvh relative overflow-hidden bg-black">
      <BackgroundVideoPlaylist
        sources={[
          "https://test-video-ai.b-cdn.net/videos/b4d9606a-f396-43df-9c59-9d3599c32b66.mp4",
        ]}
        darkOverlay={false}
      />

      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-transparent pointer-events-none" />

      <div className="relative z-10 h-full flex flex-col justify-end md:justify-center px-6 pb-10 md:pb-0">
        <div className="w-full max-w-sm mx-auto md:mx-0 md:ml-[10%] lg:ml-[15%]">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="mb-8"
          >
            <div className="mb-3">
              <Logo size="lg" className="text-white/90" />
            </div>
            <p className="text-white/40 text-sm leading-relaxed">
              Ustaw nowe hasło
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15, ease: "easeOut" }}
          >
            <div className="bg-white/[0.07] backdrop-blur-2xl border border-white/[0.09] rounded-2xl p-6 shadow-2xl">
              {done ? (
                <div className="text-center space-y-4 py-2">
                  <div className="flex justify-center">
                    <div className="size-12 rounded-full bg-green-500/10 flex items-center justify-center">
                      <CheckCircle className="size-6 text-green-400" />
                    </div>
                  </div>
                  <h2 className="text-base font-semibold text-white">
                    Hasło zmienione
                  </h2>
                  <p className="text-sm text-white/40">
                    Możesz się teraz zalogować nowym hasłem.
                  </p>
                  <Link
                    to="/login"
                    className="inline-block bg-accent rounded-xl px-6 py-2 text-sm font-medium text-white shadow-glow hover:shadow-glow-strong transition-all mt-2"
                  >
                    Zaloguj się
                  </Link>
                </div>
              ) : (
                <form onSubmit={(e) => { void handleSubmit(e); }} className="space-y-4">
                  <div>
                    <label className="block text-[10px] font-medium uppercase tracking-[1.5px] text-white/30 mb-2">
                      Nowe hasło
                    </label>
                    <input
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => { setPassword(e.target.value); }}
                      required
                      className="w-full bg-white/[0.06] border border-white/[0.08] rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-white/25 outline-none focus:ring-2 focus:ring-accent/50 focus:border-transparent transition-all"
                    />
                    {password && (
                      <div className="grid grid-cols-2 gap-1 mt-2.5">
                        {rules.map((r) => (
                          <PasswordRule key={r.label} met={r.met} label={r.label} />
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-[10px] font-medium uppercase tracking-[1.5px] text-white/30 mb-2">
                      Powtórz hasło
                    </label>
                    <input
                      type="password"
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => { setConfirmPassword(e.target.value); }}
                      required
                      className={clsx(
                        "w-full bg-white/[0.06] border rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-white/25 outline-none focus:ring-2 focus:ring-accent/50 focus:border-transparent transition-all",
                        confirmPassword && !passwordsMatch
                          ? "border-red-500/50"
                          : "border-white/[0.08]",
                      )}
                    />
                    {confirmPassword && !passwordsMatch && (
                      <p className="text-[11px] text-red-400 mt-1.5">
                        Hasła nie są identyczne
                      </p>
                    )}
                  </div>

                  {resetConfirm.isError && (
                    <p className="text-xs text-red-400 text-center">
                      Link wygasł lub jest nieprawidłowy.
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={resetConfirm.isPending || !canSubmit}
                    className="w-full bg-accent rounded-xl py-2.5 text-sm font-medium text-white shadow-glow hover:shadow-glow-strong hover:brightness-110 disabled:opacity-50 disabled:shadow-none transition-all cursor-pointer"
                  >
                    {resetConfirm.isPending ? "Zmienianie..." : "Zmień hasło"}
                  </button>

                  <div className="text-center">
                    <Link
                      to="/login"
                      className="text-xs text-white/30 hover:text-white/60 transition-colors"
                    >
                      ← Wróć do logowania
                    </Link>
                  </div>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
